import * as React from "react";
import { Flex, IconButton, Popover, SegmentedControl, Text } from "@radix-ui/themes";
import { MinusIcon, PlusIcon } from "@radix-ui/react-icons";
import {
  ReaderPageChoice,
  ReaderWidth,
  textSteps,
  updateReaderSettings,
  useReaderSettings,
} from "./readerSettings.ts";

/** The Aa button: text size, typeface, the page's colours and how wide the lines run. */
export const ReaderSettingsPopover: React.FC = () => {
  const settings = useReaderSettings();
  const step = Math.min(Math.max(settings.step, 0), textSteps.length - 1);
  return (
    <Popover.Root>
      <Popover.Trigger>
        <button type="button" className="icon-button reader-aa" aria-label="Text and page" title="Text and page">
          Aa
        </button>
      </Popover.Trigger>
      <Popover.Content align="end" width="300px">
        <Flex direction="column" gap="4">
          <Row label="Size">
            <Flex align="center" gap="3">
              <IconButton
                variant="soft"
                color="gray"
                aria-label="Smaller text"
                disabled={step == 0}
                onClick={() => updateReaderSettings({ step: step - 1 })}
              >
                <MinusIcon />
              </IconButton>
              <Text size="2" color="gray" style={{ minWidth: 40, textAlign: "center" }}>
                {Math.round((textSteps[step] ?? 1) * 100)}%
              </Text>
              <IconButton
                variant="soft"
                color="gray"
                aria-label="Bigger text"
                disabled={step == textSteps.length - 1}
                onClick={() => updateReaderSettings({ step: step + 1 })}
              >
                <PlusIcon />
              </IconButton>
            </Flex>
          </Row>
          <Row label="Typeface">
            <SegmentedControl.Root
              value={settings.sans ? "sans" : "serif"}
              onValueChange={(value) => updateReaderSettings({ sans: value == "sans" })}
            >
              <SegmentedControl.Item value="serif">Serif</SegmentedControl.Item>
              <SegmentedControl.Item value="sans">Sans</SegmentedControl.Item>
            </SegmentedControl.Root>
          </Row>
          <Row label="Page">
            <SegmentedControl.Root
              value={settings.page}
              onValueChange={(value) => updateReaderSettings({ page: value as ReaderPageChoice })}
            >
              <SegmentedControl.Item value="app">Auto</SegmentedControl.Item>
              <SegmentedControl.Item value="paper">Paper</SegmentedControl.Item>
              <SegmentedControl.Item value="white">White</SegmentedControl.Item>
              <SegmentedControl.Item value="dark">Dark</SegmentedControl.Item>
            </SegmentedControl.Root>
          </Row>
          <Row label="Width">
            <SegmentedControl.Root
              value={settings.width}
              onValueChange={(value) => updateReaderSettings({ width: value as ReaderWidth })}
            >
              <SegmentedControl.Item value="narrow">Narrow</SegmentedControl.Item>
              <SegmentedControl.Item value="medium">Medium</SegmentedControl.Item>
              <SegmentedControl.Item value="wide">Wide</SegmentedControl.Item>
            </SegmentedControl.Root>
          </Row>
          <Text size="1" color="gray">
            Auto is the dark page when the app's dark, and paper when it's light.
          </Text>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Flex direction="column" gap="2">
    <Text size="2" weight="bold">
      {label}
    </Text>
    {children}
  </Flex>
);
